// OWNER: platform module (analytics area). Turns the current URL into a route-PATTERN slug for AN-1 screen views.
//   /w/<uuid>/money/gifts/<uuid>?new=1  →  money/gifts/:id
// Never ids, query strings, hashes or free text: every segment that is a route parameter value, or that contains a
// digit or anything other than lowercase letters, `-` and `_`, becomes `:id`. The result must also pass the shared
// `isSafeScreenSlug` check (the server rejects anything else) and be one of the app's screens (`SCREEN_ROUTES`,
// SR-29 — the server drops anything else), otherwise nothing is sent.
import { isKnownScreen, isSafeScreenSlug } from '@seventide/types';

const WORD = /^[a-z][a-z_-]{0,31}$/;

export function screenSlugFromPath(
  pathname: string,
  params: Record<string, string | undefined> = {},
): string | null {
  const paramValues = new Set(
    Object.entries(params)
      .filter(([k, v]) => k !== '*' && typeof v === 'string' && v.length > 0)
      .map(([, v]) => v!),
  );
  let segments = pathname
    .split(/[?#]/)[0]!
    .split('/')
    .filter(Boolean)
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return s;
      }
    });
  // Drop the workspace prefix: /w/:wsId/...
  if (segments[0] === 'w' && segments.length >= 2) segments = segments.slice(2);
  if (segments.length === 0) return 'home';
  const out = segments.map((s) => (paramValues.has(s) || !WORD.test(s) ? ':id' : s));
  // Collapse runs of placeholders (e.g. a splat with several segments).
  const collapsed = out.filter((s, i) => !(s === ':id' && out[i - 1] === ':id'));
  const slug = collapsed.join('/').slice(0, 64);
  return isSafeScreenSlug(slug) && isKnownScreen(slug) ? slug : null;
}

/** Respect the browser's Do Not Track / Global Privacy Control signals: no screen views are sent. */
export function trackingDisabledByBrowser(nav: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator): boolean {
  if (!nav) return true;
  const n = nav as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string };
  const w = typeof window === 'undefined' ? undefined : (window as Window & { doNotTrack?: string });
  return n.doNotTrack === '1' || n.doNotTrack === 'yes' || n.msDoNotTrack === '1' || w?.doNotTrack === '1' || n.globalPrivacyControl === true;
}
